import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('livingTrustPreferences');
export interface LivingTrustPreferencesInfoRec {
  id: string;
  userId: string;
  hasLivingTrust?: boolean;
  hasLivingWill?: boolean;
  trustLastUpdated?: string;
  trustLocation?: string;
  trustExecutor?: string;
  trustExecutorEmail?: string;
  trustExecutorNotified?: string;
  trustAdditionalDetails?: string;
  willLastUpdated?: string;
  willLocation?: string;
  willExecutor?: string;
  willExecutorEmail?: string;
  willExecutorNotified?: string;
  willAdditionalDetails?: string;
  notes?: string;
  isLocked?: boolean;
}

class LivingTrustPreferencesInfoService {
  async getAllLivingTrustPreferences(): Promise<LivingTrustPreferencesInfoRec[]> {
    const response = await http.get('/api/living-trust-preferences');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch living trust preferences information');
    }

    return response.data.data || [];
  }

  async getLivingTrustPreferencesById(id: string): Promise<LivingTrustPreferencesInfoRec> {
    const response = await http.get(`/api/living-trust-preferences/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch living trust preferences information');
    }

    return response.data.data;
  }

  async createLivingTrustPreferences(data: Omit<LivingTrustPreferencesInfoRec, 'id' | 'userId'>): Promise<LivingTrustPreferencesInfoRec> {
    const response = await http.post('/api/living-trust-preferences', data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create living trust preferences information');
    }

    return response.data.data;
  }

  async updateLivingTrustPreferences(id: string, data: Partial<LivingTrustPreferencesInfoRec>): Promise<LivingTrustPreferencesInfoRec> {
    const response = await http.patch(`/api/living-trust-preferences/${id}`, data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update living trust preferences information');
    }

    return response.data.data;
  }

  async deleteLivingTrustPreferences(id: string): Promise<void> {
    const response = await http.delete(`/api/living-trust-preferences/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete living trust preferences information');
    }
  }

  async lockLivingTrustPreferences(id: string): Promise<void> {
    const response = await http.post(`/api/living-trust-preferences/${id}/lock`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock living trust preferences information');
    }
  }

  async unlockLivingTrustPreferences(id: string, emails: string[]): Promise<void> {
    const response = await http.post(`/api/living-trust-preferences/${id}/unlock`, { emails });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock living trust preferences information');
    }
  }

  async getLivingTrustPreferencesAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/living-trust-preferences/${id}/grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to get living trust preferences access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching living trust preferences access grants:', error);
      throw error;
    }
  }
}

export const livingTrustPreferencesService = new LivingTrustPreferencesInfoService();